import { useState, type ComponentType } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { Annotation, Microphone01, Star01 } from '@kula-ai/icons-core';
import { RichHtml } from '@/ui/SanitizedDom';
import { aspectLabel } from './candidateCopy';

type Evidence = { aspect: string; snippet: string; rating?: number };

type AspectStyle = { accent: string; Icon: ComponentType<{ size?: number }> };

// Per-aspect left-accent colour + icon; grey/annotation is the fallback.
const ASPECT_STYLE: Record<string, AspectStyle> = {
  note: { accent: 'secondary-300', Icon: Annotation },
  scorecard: { accent: 'pastel-10-dark', Icon: Star01 },
  interview: { accent: 'pastel-3-dark', Icon: Microphone01 },
  transcript: { accent: 'pastel-3-dark', Icon: Microphone01 },
};
const DEFAULT_STYLE: AspectStyle = {
  accent: 'secondary-300',
  Icon: Annotation,
};

// Long scorecard/transcript snippets clamp to a few lines behind a toggle.
const CLAMP_LINES = 3;
const EXPANDABLE_LEN = 160;

// One matched-aspect snippet as a left-accent callout: icon + small-caps label
// (with rating), sanitized rich-text body, and a show-more toggle when long.
export const EvidenceCallout = ({ evidence }: { evidence: Evidence }) => {
  const [expanded, setExpanded] = useState(false);
  const { accent, Icon } = ASPECT_STYLE[evidence.aspect] ?? DEFAULT_STYLE;
  const expandable = evidence.snippet.length > EXPANDABLE_LEN;
  const rating =
    typeof evidence.rating === 'number' ? ` · ${evidence.rating}/4` : '';

  return (
    <Flex pl="10px" borderLeft="2px solid" borderColor={accent}>
      <Box flex="1" minW={0}>
        <Flex align="center" gap="6px">
          <Box as="span" display="inline-flex" color={accent}>
            <Icon size={13} />
          </Box>
          <Text
            variant="caption"
            className="uppercase tracking-wide text-secondary-500"
          >
            {`${aspectLabel(evidence.aspect)}${rating}`}
          </Text>
        </Flex>
        <RichHtml
          html={evidence.snippet}
          mt="4px"
          fontSize="12px"
          lineHeight="1.5"
          color="secondary-800"
          noOfLines={expandable && !expanded ? CLAMP_LINES : undefined}
          sx={{ 'table, td, th': { fontSize: '12px' } }}
        />
        {expandable && (
          <Box
            as="button"
            type="button"
            mt="4px"
            onClick={() => setExpanded((v) => !v)}
          >
            <Text
              variant="caption"
              className="text-primary-600 hover:underline"
            >
              {expanded ? 'Show less' : 'Show more'}
            </Text>
          </Box>
        )}
      </Box>
    </Flex>
  );
};
